/** @jsxImportSource @emotion/react */

import { useMemo, useState } from 'react'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import MuiTooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { format } from 'date-fns'
import { Link } from 'react-router-dom'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg'
import { ConfirmationModal } from 'components/Modals/ConfirmationModal/ConfirmationModal'
import { Search } from 'components/Input/Search'
import Table, { FooterWrapper, PaginationPage, PaginationTake, TableProps } from 'components/Table'
import TabsNavigation from 'components/TabsNavigation'
import { FIRST_PAGE } from 'hooks/useQueryWithParams'
import { toast } from 'utils/toast'
import { STATUS_TABS } from './constants'
import { getDeleteDisabledReason, getSectionNames, isLinkedItem } from './helpers'
import { useBlogPostListSearchParams } from './hooks'
import * as classes from './styles'
import { BlogPostListItem, BlogPostListProps } from './types'

export const BlogPostList = <Item extends BlogPostListItem>({
  copy,
  getEditLink,
  getSectionLink,
  isDeleting,
  isLoading,
  items,
  onDelete,
  showStatusTabs = true,
  status,
  totalCount,
}: BlogPostListProps<Item>) => {
  const [itemToDelete, setItemToDelete] = useState<Item | null>(null)
  const {
    search,
    activeStatus: queryActiveStatus,
    page,
    take,
    handleSearchChange,
    handleStatusChange,
    handlePageChange,
    handleTakeChange,
  } = useBlogPostListSearchParams()
  const activeStatus = status ?? queryActiveStatus

  const itemsCount = totalCount
  const pageCount = take ? Math.ceil(itemsCount / take) : 0
  const activePage = pageCount ? Math.min(page, pageCount) : FIRST_PAGE

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) {
      return
    }

    try {
      await onDelete(itemToDelete)

      setItemToDelete(null)
      toast.success(copy.deleteSuccessMessage)
    } catch (error) {
      toast.error(copy.deleteErrorMessage)
    }
  }

  const columns: TableProps<Item>['columns'] = useMemo(
    () => [
      {
        title: copy.titleColumn,
        renderCell: (item) => (
          <Typography variant="body2" css={classes.titleText}>
            {item.title}
          </Typography>
        ),
        flex: '2',
      },
      {
        title: 'Author',
        renderCell: (item) => <Typography variant="body2">{item.author.fullName}</Typography>,
        flex: '1',
      },
      {
        title: 'Last Modified Date',
        renderCell: (item) => (
          <Typography variant="body2">{format(new Date(item.updatedAt), 'MMM d, yyyy')}</Typography>
        ),
        flex: '1',
      },
      {
        title: 'Linked Sections',
        renderCell: (item) => {
          const sectionNames = getSectionNames(item)

          if (!sectionNames.length) {
            return <Typography variant="body2">&mdash;</Typography>
          }

          return (
            <div css={classes.sectionsList}>
              {item.section.map((section) => (
                <MuiTooltip key={section.id} title={`Open ${section.title} section`} arrow>
                  <Chip
                    component={Link}
                    to={getSectionLink(section, item)}
                    label={section.title}
                    size="small"
                    css={classes.sectionChip}
                    clickable
                  />
                </MuiTooltip>
              ))}
            </div>
          )
        },
        flex: '2',
      },
      {
        title: '',
        renderCell: (item) => {
          const isLinked = isLinkedItem(item)
          const canOpenDeleteTooltip = isLinked
          const deleteButton = (
            <IconButton
              size="small"
              css={[classes.actionButton, classes.deleteButton]}
              disabled={isLinked || isDeleting}
              onClick={() => setItemToDelete(item)}
            >
              <TrashIcon />
            </IconButton>
          )

          return (
            <div css={classes.actions}>
              <IconButton component={Link} size="small" css={classes.actionButton} to={getEditLink(item)}>
                <EditIcon />
              </IconButton>
              <MuiTooltip
                title={canOpenDeleteTooltip ? getDeleteDisabledReason(item, copy.entityName) : ''}
                disableHoverListener={!canOpenDeleteTooltip}
                disableFocusListener={!canOpenDeleteTooltip}
                disableTouchListener={!canOpenDeleteTooltip}
                arrow
              >
                <span css={classes.actionTooltipTarget}>{deleteButton}</span>
              </MuiTooltip>
            </div>
          )
        },
        flex: '0 1 112px',
      },
    ],
    [copy.entityName, copy.titleColumn, getEditLink, getSectionLink, isDeleting]
  )

  return (
    <div>
      <div css={[classes.filtersSection, !showStatusTabs && classes.filtersSectionOnlySearch]}>
        {showStatusTabs && (
          <TabsNavigation
            css={classes.statusTabs}
            tabs={STATUS_TABS}
            value={activeStatus}
            onChange={handleStatusChange}
          />
        )}
        <Search css={classes.search} value={search} onChange={handleSearchChange} />
      </div>

      <Table css={classes.table} columns={columns} items={items} isNotLoaded={isLoading} wrapperMinWidth={900} />

      <FooterWrapper>
        <PaginationTake take={take} onTakeChange={handleTakeChange} />
        <PaginationPage totalCount={itemsCount} count={pageCount} page={activePage} onChange={handlePageChange} />
      </FooterWrapper>

      <ConfirmationModal
        open={!!itemToDelete}
        title={copy.deleteTitle}
        subtitle={copy.deleteConfirmationSubtitle}
        confirmText="Confirm Deleting"
        isLoading={isDeleting}
        onClose={() => setItemToDelete(null)}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  )
}
